import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthResponse, SigninData, SignupData, User } from '../components/auth/models/user.interface';
import { AppSettingsService } from '../config/config-service';
import { TokenIdService } from './token-id.service';
import { CommonService } from './common.service';

export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  investmentGoals: string;
  lastLogin: string;
  createdAt: string;
}
export interface TransactionFilters {
  page?: number;
  limit?: number;
  status?: 'Pending' | 'Approved' | 'Rejected';
  userId?: any;
  portfolioId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
apiUrl:string | undefined;

  constructor( private http: CommonService,  private appSettingsService: AppSettingsService, private tokenIdService: TokenIdService) {
   this.apiUrl =  this.appSettingsService.getSettings().apiEndPoint || ''
  }

  signup(userData: SignupData): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/api/auth/signup`, userData).pipe(
      catchError(this.handleError)
    );
  }

  signin(credentials: SigninData): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/api/auth/signin`, credentials).pipe(
      catchError(this.handleError)
    );
  }

  updateProfile(userData: UserProfile): Observable<UserProfile> {
    return this.http.patch<UserProfile>(`${this.apiUrl}/api/auth/updateProfile`, userData);
  }


  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    try {
      const response = await this.http.post<AuthResponse>(`${this.apiUrl}/auth/refresh`, { refreshToken }).toPromise();
      return response!;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async logout(): Promise<void> {
    try {
      await this.http.post(`${this.apiUrl}/auth/logout`, {}).toPromise();
      // Clear tokens from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    } catch (error) {
      // Even if logout fails on server, clear local tokens
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      throw this.handleError(error);
    }
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<{ data: User }>(`${this.apiUrl}/user/profile`).pipe(
      map(response => response.data),
    //   catchError(this.handleError)
    );
  }

  getProfile(userId: string): Observable<UserProfile> {
    return this.http.post<{ success: boolean; message: string; data: { user: UserProfile } }>(
      `${this.apiUrl}/api/auth/getProfile`,
      { userId },
      { headers: { 'Content-Type': 'application/json' } }
    ).pipe(
      map(response => response.data.user),
      catchError(this.handleError)
    );
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  decodeToken(token: string): any {
    return this.tokenIdService.decodeToken(token);
  }

  getUserIdFromToken(token?: string): string | null {
    return this.tokenIdService.getUserIdFromToken(token);
  }

  getRoleTypeFromToken(token?: string): string | null {
    return this.tokenIdService.getRoleTypeFromToken(token);
  }
  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }
  getDashboard(userId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/dashboard`, { userId }, {
      headers: { 'Content-Type': 'application/json' }
    });
  }
  
  // Investment analytics
  getInvestmentAnalytics(): Observable<{
    success: boolean;
    data: {
      portfolioAllocation: Array<{ portfolioName: string; amount: number; percentage: number }>;
      riskAssessment: { label: string; riskScore: number; volatility: string; diversification: string };
    };
  }> {
    return this.http.get(`${this.apiUrl}/api/auth/getInvestmentAnalytics`);
  }
  
  private handleError(error: any) {
    let errorMessage = 'An error occurred. Please try again.';
    if (error instanceof HttpErrorResponse) {
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status === 401) {
        errorMessage = 'Invalid credentials. Please check your email and password.';
      } else if (error.status === 409) {
        errorMessage = 'An account with this email already exists.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
    }
    return throwError(() => new Error(errorMessage));
  }
  getAllUserInvestments(userId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/userInvestments/getAllUserInvestments?userId=${userId}`);
  }
  getAllUserTransactions(page: number = 1, limit: number = 20): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/transactions/getAllTransactions?page=${page}&limit=${limit}`);
  }
  getUserTransactions(filters: TransactionFilters = {}): Observable<any> {
    // Use HttpParams for safe and easy query parameter construction
    let params = new HttpParams();

    // Dynamically append parameters if they exist in the filters object
    if (filters.page) {
      params = params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params = params.append('limit', filters.limit.toString());
    }
    if (filters.status) {
      params = params.append('status', filters.status);
    }
    if (filters.userId) {
      params = params.append('userId', filters.userId);
    }
    if (filters.portfolioId) {
      params = params.append('portfolioId', filters.portfolioId);
    }

    // Make the GET request with the constructed parameters
    return this.http.get(`${this.apiUrl}/api/transactions/getAllTransactions`, { params });
  }

 
  cancelTransaction(transactionId: string): Observable<{ success: boolean; message: string }> {
    if (!transactionId) {
      return throwError(() => new Error('Transaction ID is required'));
    }
    
    return this.http.patch<{ success: boolean; message: string }>(
      `${this.apiUrl}/api/transactions/cancelTransaction/${transactionId}`,
      {}
    ).pipe(
      catchError(this.handleError)
    );
  }
}